import { useState, useEffect, useRef } from 'react'
import {
  CodeEditor,
  QueryButtonsSection,
  ExecutionResults as ExecutionResultsType,
  CodeType,
  CodeTypeValue,
  QuestionClass,
} from '@stratascratch/code-editor'
import EditorCaptionBar from './EditorCaptionBar'
import ExecutionResults from './ExecutionResults'
import CODE_TYPES from '@stratascratch/code-editor/dist/consts'
import {
  PythonMessage,
  AuthMessage,
  LoadErrorMessage,
} from './ExecutionMessages'
import TestTabs, { Test } from './TestTabs'

const getSolutionState = (results: ExecutionResultsType | null) => {
  const isCorrect = results?.is_correct_solution
  const callToUpgrade = results?.call_to_upgrade

  if (isCorrect !== null && isCorrect !== undefined && !callToUpgrade) {
    if (isCorrect) {
      return 'solved'
    }
    return 'failed'
  }
  return 'default'
}

interface CodeSnippet {
  codeType: CodeType
  code: string
}

export interface ExecuteCodeParams {
  code: string
  check_solution: boolean
  question_id: number
  code_type: CodeTypeValue
}

export interface CodeEditorWidgetProps {
  isAuthorized: boolean
  executeCode: (
    params: ExecuteCodeParams,
  ) => Promise<ExecutionResultsType | null>
  questionClass: QuestionClass
  questionId: number
  questionUrl: string | null | undefined
  questionTables: string[] | null | undefined
  checkSolution: boolean
  codeSnippets: CodeSnippet[]
  algorithmTests: {
    tests: Test[] | undefined
    selectedTest: Test | undefined
    setSelectedTest: (test: Test) => void | undefined
  }
}

function BaseCodeEditorWidget({
  isAuthorized,
  executeCode,
  questionClass,
  questionId,
  questionUrl,
  questionTables,
  checkSolution,
  codeSnippets,
  algorithmTests,
}: CodeEditorWidgetProps) {
  const [codeType, setCodeType] = useState(codeSnippets[0].codeType)
  const [code, setCode] = useState(codeSnippets[0].code)
  const [isLoading, setIsLoading] = useState(false)
  const [results, setResults] = useState<ExecutionResultsType | null>(null)

  const getInitialCode = (ct: CodeType) =>
    codeSnippets.find((snip) => snip.codeType === ct)?.code

  const resetCode = () => {
    const initialCode = getInitialCode(codeType)
    if (initialCode) setCode(initialCode)
    if (algorithmTests.tests)
      algorithmTests.setSelectedTest(algorithmTests.tests[0])
    setResults(null)
  }

  useEffect(() => {
    resetCode()
  }, [codeType])

  const runExecution = async (check: boolean) => {
    if (!isAuthorized) return

    setIsLoading(true)
    setResults(null)
    const params = {
      code,
      check_solution: check,
      question_id: questionId,
      code_type: CODE_TYPES[codeType].value,
    }
    try {
      const result = await executeCode(params)
      setResults(result)
    } catch {
      setResults({ error: 'An unknown error occured during execution' })
    } finally {
      setIsLoading(false)
    }
  }

  const runCode = async () => {
    runExecution(false)
  }

  const runCheckSolution = async () => {
    await runExecution(true)
  }

  const codeTypeOptions = codeSnippets?.map((snip) => snip.codeType)

  const aceMode = CODE_TYPES[codeType]?.mode

  const ref = useRef<HTMLDivElement>(null)

  return (
    <div className="my-8" ref={ref}>
      {checkSolution && questionUrl && (
        <div className="text-right">
          <a
            href={`https://platform.stratascratch.com${questionUrl}`}
            className="text-(--color-link) text-sm"
          >
            Go to the question on the platform
          </a>
        </div>
      )}
      <div className="relative">
        <EditorCaptionBar
          codeType={codeType}
          codeTypeOptions={codeTypeOptions}
          onChange={setCodeType}
          tables={questionTables}
        />
        <CodeEditor value={code} onChange={setCode} mode={aceMode} />
        {!isAuthorized && <AuthMessage />}
        {!questionId && <LoadErrorMessage />}
      </div>
      {questionClass !== 'Algorithm Question' && codeType === 'PYTHON' && (
        <PythonMessage />
      )}
      <QueryButtonsSection
        checkSolution={checkSolution ? runCheckSolution : undefined}
        solutionState={getSolutionState(results)}
        resetCode={resetCode}
        runCode={runCode}
        isLoading={isLoading}
        disabled={!questionId || !isAuthorized}
        keyBindingRef={ref}
        buttonsLayoutClassName="px-0 pt-0 my-4"
      >
        {questionClass === 'Algorithm Question' &&
          algorithmTests.selectedTest && (
            <TestTabs
              tests={algorithmTests.tests}
              selectedTest={algorithmTests.selectedTest}
              setSelectedTest={algorithmTests.setSelectedTest}
            />
          )}
      </QueryButtonsSection>
      <ExecutionResults
        isLoading={isLoading}
        questionClass={questionClass}
        results={results}
      />
    </div>
  )
}

export default BaseCodeEditorWidget
